import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Where the recorder is. `unsupported` is decided once, at mount; `denied` sticks
 * until the user changes the browser/OS permission, so the button can explain itself
 * instead of failing again on every tap.
 */
export type VoiceRecorderStatus =
  | 'unsupported'
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'denied'
  | 'error';

export type VoiceRecording = {
  blob: Blob;
  mimeType: string;
  /** Extension that matches the container — the transcription vendor sniffs by it. */
  extension: string;
  durationMs: number;
};

type UseVoiceRecorderOptions = {
  /** Hard stop, in seconds. Mirrors the server limit so a long note never bounces. */
  maxDurationSeconds?: number;
  onRecording: (recording: VoiceRecording) => void;
};

/**
 * MediaRecorder container by platform: Chrome/Edge/Android WebView record webm/opus,
 * Safari and the iOS WebView record mp4 (AAC). First supported wins; the extension
 * follows the container so the server can route it to the right model.
 */
const CANDIDATE_TYPES: { mimeType: string; extension: string }[] = [
  { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
  { mimeType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/mp4', extension: 'm4a' },
  { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
];

/**
 * Speech, not music: one channel at a voice bitrate is all the transcription model
 * uses, and the browser's own processing (echo / noise / gain) is what makes a note
 * recorded on deck legible. Everything is `ideal`, so a device that cannot honour a
 * constraint still records instead of throwing OverconstrainedError. At 32 kbit/s a
 * two-minute note is ~480 KB instead of ~2 MB at the Chrome default (128 kbit/s),
 * which is most of the upload time on a marina Wi-Fi.
 */
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: { ideal: 1 },
  sampleRate: { ideal: 16000 },
  echoCancellation: { ideal: true },
  noiseSuppression: { ideal: true },
  autoGainControl: { ideal: true },
};

const AUDIO_BITS_PER_SECOND = 32_000;

const isSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia;

const pickType = () => {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return CANDIDATE_TYPES[0];
  return CANDIDATE_TYPES.find((c) => MediaRecorder.isTypeSupported(c.mimeType)) ?? null;
};

const isPermissionError = (err: unknown) =>
  err instanceof DOMException &&
  (err.name === 'NotAllowedError' ||
    err.name === 'PermissionDeniedError' ||
    err.name === 'SecurityError');

/**
 * Tap-to-start / tap-to-stop voice recording on top of MediaRecorder.
 *
 * Owns the media stream and the recorder; releases the microphone the moment recording
 * stops (or the component unmounts) so the browser's "recording" indicator goes away.
 * The finished note is handed to `onRecording` as a Blob — what to do with it (send it
 * for transcription) is the caller's business.
 */
export const useVoiceRecorder = ({
  maxDurationSeconds = 120,
  onRecording,
}: UseVoiceRecorderOptions) => {
  const [status, setStatus] = useState<VoiceRecorderStatus>(() =>
    isSupported() ? 'idle' : 'unsupported',
  );
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const discardRef = useRef(false);
  const onRecordingRef = useRef(onRecording);
  useEffect(() => {
    onRecordingRef.current = onRecording;
  }, [onRecording]);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    tickRef.current = null;
    stopTimerRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (status === 'recording' || status === 'requesting' || status === 'unsupported') return;

    const type = pickType();
    if (!type) {
      setStatus('unsupported');
      return;
    }

    setStatus('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
    } catch (err) {
      setStatus(isPermissionError(err) ? 'denied' : 'error');
      return;
    }

    // Safari ignores audioBitsPerSecond and keeps its AAC default; Chrome and the
    // Android WebView honour it for Opus.
    const recorder = new MediaRecorder(stream, {
      mimeType: type.mimeType,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    });
    chunksRef.current = [];
    discardRef.current = false;
    streamRef.current = stream;
    recorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      releaseStream();
      recorderRef.current = null;
      setStatus('error');
    };
    recorder.onstop = () => {
      const durationMs = Date.now() - startedAtRef.current;
      const blob = new Blob(chunksRef.current, { type: type.mimeType.split(';')[0] });
      releaseStream();
      recorderRef.current = null;
      setElapsedSeconds(0);
      setStatus('idle');
      if (!discardRef.current && blob.size > 0) {
        onRecordingRef.current({
          blob,
          mimeType: blob.type,
          extension: type.extension,
          durationMs,
        });
      }
    };

    startedAtRef.current = Date.now();
    setElapsedSeconds(0);
    recorder.start();
    setStatus('recording');

    tickRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    stopTimerRef.current = setTimeout(stop, maxDurationSeconds * 1000);
  }, [maxDurationSeconds, releaseStream, status, stop]);

  // Never leave the microphone open behind an unmounted button.
  useEffect(() => {
    return () => {
      discardRef.current = true;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      releaseStream();
    };
  }, [releaseStream]);

  return { status, elapsedSeconds, maxDurationSeconds, start, stop, cancel };
};
